// ForestCoverAnalysis.js - Updated with GFW API Integration
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDraw } from "./MapDrawController";

const ForestCoverAnalysis = ({ map }) => {
  const { polygonArea } = useDraw();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  // Define which tree cover density values to use (≥30%)
  const treeCoverDensityThreshold = 30;
  const minTreeHeight = 5; // in meters

  // Create the analysis box with vanilla JS
  const createForestCoverBox = (loading = true, results = null, error = null) => {
    console.log("Creating forest cover analysis box");

    // Remove any existing box
    const existingBox = document.getElementById('forest-cover-analysis-box');
    if (existingBox) {
      existingBox.remove();
    }

    // Create container
    const box = document.createElement('div');
    box.id = 'forest-cover-analysis-box';

    // Style it
    Object.assign(box.style, {
      position: 'fixed',
      bottom: '180px', // Position above the other analysis box
      left: '50%',
      transform: 'translateX(-50%)',
      width: '360px',
      padding: '20px',
      background: 'white',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 0 0 2px #2e3a59',
      zIndex: '99998', // High z-index but below the other box
      fontFamily: "'Figtree', sans-serif, Arial",
      borderRadius: '12px',
      color: '#2e3a59',
      maxHeight: '80vh',
      overflowY: 'auto'
    });

    // Create content container
    const content = document.createElement('div');

    // Header
    const header = document.createElement('div');
    Object.assign(header.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px'
    });

    const title = document.createElement('h3');
    title.textContent = 'Forest Cover Analysis';
    Object.assign(title.style, {
      margin: '0',
      fontSize: '18px',
      fontWeight: '700',
      color: '#2e3a59'
    });

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.onclick = () => box.remove();
    Object.assign(closeBtn.style, {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '18px',
      fontWeight: 'bold'
    });

    header.appendChild(title);
    header.appendChild(closeBtn);
    content.appendChild(header);

    // Content based on state
    if (error) {
      const errorDiv = document.createElement('div');
      errorDiv.textContent = error;
      Object.assign(errorDiv.style, {
        padding: '10px',
        backgroundColor: '#ffebee',
        color: '#d32f2f',
        borderRadius: '6px',
        marginBottom: '10px'
      });
      content.appendChild(errorDiv);
    } else if (loading) {
      const loadingDiv = document.createElement('div');
      Object.assign(loadingDiv.style, {
        padding: '20px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      });

      const loadingText = document.createElement('div');
      loadingText.textContent = 'Analyzing forest cover...';
      loadingText.style.marginBottom = '15px';

      // Create spinner
      const spinner = document.createElement('div');
      Object.assign(spinner.style, {
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db',
        animation: 'spin 1s linear infinite'
      });

      loadingDiv.appendChild(loadingText);
      loadingDiv.appendChild(spinner);
      content.appendChild(loadingDiv);

      // Add keyframe animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);

    } else if (results) {
      // Results section
      const resultsContainer = document.createElement('div');

      // Selected Area
      const areaSection = document.createElement('div');
      areaSection.style.marginBottom = '15px';

      const areaLabel = document.createElement('div');
      areaLabel.textContent = 'Selected Area (ha):';
      areaLabel.style.fontWeight = '600';
      areaLabel.style.marginBottom = '5px';

      const areaValue = document.createElement('div');
      areaValue.textContent = results.totalArea.toFixed(2);
      Object.assign(areaValue.style, {
        padding: '10px',
        background: '#f7f9fc',
        borderRadius: '6px',
        border: '1px solid #e1e5eb'
      });

      areaSection.appendChild(areaLabel);
      areaSection.appendChild(areaValue);
      resultsContainer.appendChild(areaSection);

      // Forest Cover Area
      const forestAreaSection = document.createElement('div');
      forestAreaSection.style.marginBottom = '15px';

      const forestAreaLabel = document.createElement('div');
      forestAreaLabel.textContent = 'Forest Cover Area (ha):';
      forestAreaLabel.style.fontWeight = '600';
      forestAreaLabel.style.marginBottom = '5px';

      const forestAreaValue = document.createElement('div');
      forestAreaValue.textContent = results.forestArea.toFixed(2);
      Object.assign(forestAreaValue.style, {
        padding: '10px',
        background: '#f7f9fc',
        borderRadius: '6px',
        border: '1px solid #e1e5eb'
      });

      forestAreaSection.appendChild(forestAreaLabel);
      forestAreaSection.appendChild(forestAreaValue);
      resultsContainer.appendChild(forestAreaSection);

      // Forest Cover Percentage
      const percentSection = document.createElement('div');
      percentSection.style.marginBottom = '15px';

      const percentLabel = document.createElement('div');
      percentLabel.textContent = 'Forest Cover Percentage (B):';
      percentLabel.style.fontWeight = '600';
      percentLabel.style.marginBottom = '5px';

      const percentValue = document.createElement('div');
      Object.assign(percentValue.style, {
        padding: '10px',
        background: '#f7f9fc',
        borderRadius: '6px',
        border: '1px solid #e1e5eb'
      });

      const percentStrong = document.createElement('strong');
      percentStrong.textContent = `${results.forestCoverPercentage.toFixed(2)}%`;

      const percentNote = document.createElement('span');
      percentNote.textContent = ' B = (Forest Cover ≥30% ≥5m / Total Area) * 100';
      Object.assign(percentNote.style, {
        fontSize: '12px',
        display: 'block',
        marginTop: '5px'
      });

      percentValue.appendChild(percentStrong);
      percentValue.appendChild(percentNote);
      percentSection.appendChild(percentLabel);
      percentSection.appendChild(percentValue);
      resultsContainer.appendChild(percentSection);

      // Add component metrics if available
      if (results.treeCoverDensityArea !== undefined && results.treeHeightArea !== undefined) {
        const componentSection = document.createElement('div');
        componentSection.style.marginBottom = '15px';

        const componentTitle = document.createElement('div');
        componentTitle.textContent = 'Component Metrics:';
        componentTitle.style.fontWeight = '600';
        componentTitle.style.marginBottom = '10px';
        componentSection.appendChild(componentTitle);

        // Tree cover density
        const densityDiv = document.createElement('div');
        densityDiv.style.marginBottom = '10px';
        densityDiv.innerHTML = `<span style="display:inline-block;width:8px;height:8px;background:#7cb342;border-radius:50%;margin-right:5px;"></span> Tree Cover Density ≥${treeCoverDensityThreshold}%: <strong>${results.treeCoverDensityArea.toFixed(2)}</strong> ha`;
        componentSection.appendChild(densityDiv);

        // Tree height
        const heightDiv = document.createElement('div');
        heightDiv.innerHTML = `<span style="display:inline-block;width:8px;height:8px;background:#1b5e20;border-radius:50%;margin-right:5px;"></span> Tree Height ≥${minTreeHeight}m: <strong>${results.treeHeightArea.toFixed(2)}</strong> ha`;
        componentSection.appendChild(heightDiv);

        resultsContainer.appendChild(componentSection);
      }

      // Carbon data if available
      if (results.carbonStock !== undefined) {
        const carbonSection = document.createElement('div');
        carbonSection.style.marginBottom = '15px';

        const carbonLabel = document.createElement('div');
        carbonLabel.textContent = 'Carbon Stock (Mg C):';
        carbonLabel.style.fontWeight = '600';
        carbonLabel.style.marginBottom = '5px';

        const carbonValue = document.createElement('div');
        carbonValue.textContent = results.carbonStock.toLocaleString(undefined, {maximumFractionDigits: 0});
        Object.assign(carbonValue.style, {
          padding: '10px',
          background: '#f7f9fc',
          borderRadius: '6px',
          border: '1px solid #e1e5eb'
        });

        carbonSection.appendChild(carbonLabel);
        carbonSection.appendChild(carbonValue);
        resultsContainer.appendChild(carbonSection);
      }

      // Recent loss if available
      if (results.recentLoss !== undefined) {
        const lossSection = document.createElement('div');
        lossSection.style.marginBottom = '15px';

        const lossLabel = document.createElement('div');
        lossLabel.textContent = 'Recent Forest Loss (ha, last 5 years):';
        lossLabel.style.fontWeight = '600';
        lossLabel.style.marginBottom = '5px';

        const lossValue = document.createElement('div');
        lossValue.textContent = results.recentLoss.toFixed(2);
        Object.assign(lossValue.style, {
          padding: '10px',
          background: '#fff9e6',
          borderRadius: '6px',
          border: '1px solid #ffe0b2',
          color: '#e65100'
        });

        lossSection.appendChild(lossLabel);
        lossSection.appendChild(lossValue);
        resultsContainer.appendChild(lossSection);
      }

      // Summary message
      const summarySection = document.createElement('div');
      const forestCoverPercentage = results.forestCoverPercentage;
      Object.assign(summarySection.style, {
        padding: '15px',
        background: forestCoverPercentage > 50 ? '#e6f7e8' : '#fff9e6',
        borderRadius: '6px',
        border: `1px solid ${forestCoverPercentage > 50 ? '#b7dfbd' : '#ffe0b2'}`,
        fontSize: '14px'
      });

      if (forestCoverPercentage > 75) {
        summarySection.textContent = "Excellent forest coverage! This area has a high percentage of forest.";
      } else if (forestCoverPercentage > 50) {
        summarySection.textContent = "Good forest coverage. This area has a moderate amount of forest.";
      } else if (forestCoverPercentage > 25) {
        summarySection.textContent = "Limited forest coverage. Consider forest conservation and restoration in this area.";
      } else {
        summarySection.textContent = "Minimal forest coverage. This area has very little forest.";
      }

      // Data source credit
      const creditDiv = document.createElement('div');
      creditDiv.style.marginTop = '15px';
      creditDiv.style.fontSize = '12px';
      creditDiv.style.color = '#666';
      creditDiv.textContent = 'Data sources: Global Forest Watch, Hansen Global Forest Change, Meta Forest Monitoring';

      resultsContainer.appendChild(summarySection);
      resultsContainer.appendChild(creditDiv);
      content.appendChild(resultsContainer);
    } else {
      const placeholderDiv = document.createElement('div');
      placeholderDiv.textContent = 'Click "Analyze Forest Cover" to view results.';
      Object.assign(placeholderDiv.style, {
        padding: '10px',
        textAlign: 'center'
      });
      content.appendChild(placeholderDiv);
    }

    // Add content to box
    box.appendChild(content);

    // Add to document body
    document.body.appendChild(box);

    return box;
  };

  // Function to add a visualization layer to the map
  const addForestVisualizationToMap = (polygon, data) => {
    if (!map) return;

    removeForestVisualizationFromMap();

    const forestCoverId = "forest-cover-analysis-layer";

    try {
      // Use try/catch for everything related to the map
      if (!map.getSource(forestCoverId)) {
        map.addSource(forestCoverId, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: polygon.geometry,
            properties: {
              forestPercentage: data.forestCoverPercentage
            }
          }
        });
      } else {
        map.getSource(forestCoverId).setData({
          type: "Feature",
          geometry: polygon.geometry,
          properties: {
            forestPercentage: data.forestCoverPercentage
          }
        });
      }

      if (!map.getLayer(forestCoverId)) {
        map.addLayer({
          id: forestCoverId,
          type: "fill",
          source: forestCoverId,
          paint: {
            "fill-color": [
              "interpolate",
              ["linear"],
              ["get", "forestPercentage"],
              0, "#ff6b6b",
              50, "#ffdd57",
              100, "#228B22"  // Dark green for forest
            ],
            "fill-opacity": 0.5,
            "fill-outline-color": "#000"
          }
        });

        map.addLayer({
          id: `${forestCoverId}-outline`,
          type: "line",
          source: forestCoverId,
          paint: {
            "line-color": "#000",
            "line-width": 2
          }
        });
      }
    } catch (err) {
      console.error("Error adding forest visualization to map:", err);
    }
  };

  // Remove visualization from map
  const removeForestVisualizationFromMap = () => {
    if (!map) return;

    const forestCoverId = "forest-cover-analysis-layer";

    try {
      if (map.getLayer(`${forestCoverId}-outline`)) {
        map.removeLayer(`${forestCoverId}-outline`);
      }

      if (map.getLayer(forestCoverId)) {
        map.removeLayer(forestCoverId);
      }

      if (map.getSource(forestCoverId)) {
        map.removeSource(forestCoverId);
      }
    } catch (err) {
      console.error("Error removing forest visualization from map:", err);
    }
  };

      // Main analysis function
  const analyzeForestCover = async (useEnhanced = false) => {
    console.log(`Analyzing forest cover for polygon of area: ${polygonArea.toFixed(2)} ha`);

    // Show loading box
    setLoading(true);
    createForestCoverBox(true);

    // Validate requirements
    if (!map || !map.__draw) {
      setError("Map or draw instance not available.");
      createForestCoverBox(false, null, "Map or draw instance not available.");
      setLoading(false);
      return;
    }

    if (polygonArea === 0) {
      setError("No polygon drawn.");
      createForestCoverBox(false, null, "No polygon drawn.");
      setLoading(false);
      return;
    }

    try {
      // Get polygon
      const features = map.__draw.getAll().features;
      if (!features || features.length === 0) {
        throw new Error("No polygon found.");
      }
      const polygon = features[0];

      // Select which API endpoint to use based on analysis type
      const endpoint = useEnhanced ? "/api/enhanced_forest_cover" : "/api/forest_cover_analysis";
      console.log(`Sending forest cover analysis request to ${endpoint}`);

      // Make API request
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          geometry: polygon.geometry,
          treeCoverDensity: treeCoverDensityThreshold,
          minHeight: minTreeHeight
        })
      });

      console.log("Forest cover API response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Forest cover API error response:", errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log("Forest cover API response data:", data);

      // Set results state
      setResults(data);

      // Add visualization to map
      addForestVisualizationToMap(polygon, data);

      // Update the box with results
      createForestCoverBox(false, data);

    } catch (err) {
      console.error("Forest cover analysis error:", err);
      setError(err.message || "Error analyzing forest cover.");
      createForestCoverBox(false, null, err.message || "Error analyzing forest cover.");
      removeForestVisualizationFromMap();
    } finally {
      setLoading(false);
    }
  };

  // Enhanced analysis with additional metrics
  const analyzeEnhancedForestCover = async () => {
    return analyzeForestCover(true);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const box = document.getElementById('forest-cover-analysis-box');
      if (box) box.remove();
      removeForestVisualizationFromMap();
    };
  }, []);

  return { analyzeForestCover, analyzeEnhancedForestCover };
};

ForestCoverAnalysis.propTypes = {
  map: PropTypes.object
};

export default ForestCoverAnalysis;
